import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  streamChat,
  regenerateChat,
  getChatHistory,
  getChatPresets,
  clearChatConversation,
  runChatScenario,
  postChatTelemetry,
  postChatFeedback,
} from '../services/loanChatApi';

/**
 * State and actions for the loan AI chat: history/preset loading, SSE streaming,
 * regeneration, server-side clearing, per-message feedback, and telemetry.
 */
export default function useLoanChat({ loanId, isOpen, quickActionsEnabled = true, quickActionsExpanded = false, scenarioEnabled = true }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [presetCategories, setPresetCategories] = useState([]);
  const [presetsError, setPresetsError] = useState(null);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState('');
  const [selectedPresetKey, setSelectedPresetKey] = useState('');
  const [conversationKey, setConversationKey] = useState('');
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const token = useSelector((state) => state.auth.token);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Load conversation history when the panel opens for a loan
  useEffect(() => {
    if (!isOpen) return;
    if (!loanId || !token) return;
    if (historyLoaded === loanId) return;

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      setHistoryError(null);
      setError(null);
      try {
        const data = await getChatHistory(loanId, token);
        setMessages((data.messages || []).map((m) => ({ id: m.id, role: m.role, content: m.content })));
        setConversationKey(data.conversationKey || '');
      } catch (err) {
        console.error('Failed to load chat history:', err);
        setHistoryError('Could not load the previous conversation.');
      } finally {
        setHistoryLoaded(loanId);
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [isOpen, loanId, token, historyLoaded]);

  // Load quick-action presets — deferred until the user expands Quick Actions so the
  // loan-aware preset call (which reads liabilities) only runs on demand.
  useEffect(() => {
    if (!quickActionsEnabled) return;
    if (!quickActionsExpanded) return;
    if (!isOpen) return;
    if (!loanId || !token) return;
    if (presetCategories.length > 0) return;

    const loadPresets = async () => {
      setIsLoadingPresets(true);
      setPresetsError(null);
      try {
        const data = await getChatPresets(loanId, token);
        setPresetCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to load AI chat presets:', err);
        setPresetsError('Could not load quick actions.');
      } finally {
        setIsLoadingPresets(false);
      }
    };

    loadPresets();
  }, [isOpen, loanId, token, presetCategories.length, quickActionsEnabled, quickActionsExpanded]);

  const trackTelemetry = useCallback(async (eventType, metadata = {}) => {
    if (!loanId || !token) return;
    try {
      await postChatTelemetry({
        loanId,
        eventType,
        intent: selectedIntent,
        presetKey: selectedPresetKey,
        conversationKey,
        metadata,
        token,
      });
    } catch (telemetryErr) {
      console.error('Failed to send AI chat telemetry:', telemetryErr);
    }
  }, [loanId, token, selectedIntent, selectedPresetKey, conversationKey]);

  // Fire panel_opened once per open (trackTelemetry identity changes must not re-fire it)
  const panelOpenedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !panelOpenedRef.current) {
      panelOpenedRef.current = true;
      trackTelemetry('panel_opened');
    }
    if (!isOpen) {
      panelOpenedRef.current = false;
    }
  }, [isOpen, trackTelemetry]);

  const submitFeedback = useCallback(async (rating, messageId) => {
    if (!loanId || !token) return;
    try {
      await postChatFeedback({ loanId, conversationKey, messageId, rating, token });
      if (messageId) {
        setFeedbackByMessageId((prev) => ({ ...prev, [messageId]: rating }));
      }
      trackTelemetry('feedback_submitted', { rating: String(rating), messageId: String(messageId || '') });
    } catch (feedbackErr) {
      console.error('Failed to submit AI feedback:', feedbackErr);
    }
  }, [loanId, token, conversationKey, trackTelemetry]);

  /**
   * Runs an SSE stream (send or regenerate) and appends the assistant reply to the messages.
   */
  const runStream = useCallback(async (startStream) => {
    setError(null);
    setStreamingContent('');
    setIsStreaming(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let accumulated = '';
    let messageId = null;

    try {
      await startStream({
        signal: abortController.signal,
        onContent: (delta) => {
          accumulated += delta;
          setStreamingContent(accumulated);
        },
        onStatus: (status) => setStatusMessage(status),
        onMessageId: (id) => {
          messageId = id;
        },
      });

      if (accumulated) {
        setMessages((prev) => [...prev, { id: messageId, role: 'assistant', content: accumulated }]);
        await trackTelemetry('response_received', { length: String(accumulated.length) });
      }
      return true;
    } catch (err) {
      if (err.name === 'AbortError') {
        // Keep whatever was streamed before the user stopped generation
        if (accumulated) {
          setMessages((prev) => [...prev, { id: messageId, role: 'assistant', content: accumulated }]);
        }
      } else {
        setError(err.message || 'Failed to send message');
        console.error('AI Chat error:', err);
      }
      return false;
    } finally {
      setStreamingContent('');
      setStatusMessage('');
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [trackTelemetry]);

  const buildScenarioContext = useCallback(async (trimmed, intent, presetKey) => {
    if (!scenarioEnabled || intent !== 'scenario') return trimmed;

    let scenarioType = '';
    let scenarioValue;
    if (presetKey === 'scenario_more_down') {
      scenarioType = 'more_down_payment';
      scenarioValue = 5;
    } else if (presetKey === 'scenario_payoff_auto') {
      scenarioType = 'payoff_auto_loan';
    } else {
      return trimmed;
    }

    try {
      const scenario = await runChatScenario({ loanId, scenarioType, value: scenarioValue, token });
      const assumptionsText = (scenario.assumptions || []).map((x) => `- ${x}`).join('\n');
      return `${trimmed}

Use these deterministic scenario results as source-of-truth values:
- Baseline LoanAmount: ${scenario.baseline?.loanAmount ?? 'n/a'}
- Baseline LTV: ${scenario.baseline?.ltvRatio ?? 'n/a'}
- Baseline DTI: ${scenario.baseline?.dtiRatio ?? 'n/a'}
- Baseline TotalPayment: ${scenario.baseline?.totalMonthlyPayment ?? 'n/a'}
- Projected LoanAmount: ${scenario.projected?.loanAmount ?? 'n/a'}
- Projected LTV: ${scenario.projected?.ltvRatio ?? 'n/a'}
- Projected DTI: ${scenario.projected?.dtiRatio ?? 'n/a'}
- Projected TotalPayment: ${scenario.projected?.totalMonthlyPayment ?? 'n/a'}
${assumptionsText ? `Assumptions:\n${assumptionsText}` : ''}`;
    } catch (scenarioErr) {
      console.error('Failed to run scenario model:', scenarioErr);
      return trimmed;
    }
  }, [scenarioEnabled, loanId, token]);

  // Core send: appends the user message, builds any scenario context, and streams the reply.
  const submitMessage = useCallback(async (text, intent, presetKey) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isStreaming || !loanId) return;

    setMessages((prev) => [...prev, { id: null, role: 'user', content: trimmed }]);

    const aiMessage = await buildScenarioContext(trimmed, intent, presetKey);
    await trackTelemetry('message_sent');

    await runStream(({ signal, onContent, onStatus, onMessageId }) =>
      streamChat({ loanId, message: aiMessage, intent: intent || undefined, token, signal, onContent, onStatus, onMessageId }));
  }, [isStreaming, loanId, token, buildScenarioContext, trackTelemetry, runStream]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming || !loanId) return;

    const text = input;
    const intent = selectedIntent;
    const presetKey = selectedPresetKey;
    setInput('');
    setSelectedIntent('');
    setSelectedPresetKey('');

    await submitMessage(text, intent, presetKey);
  }, [input, isStreaming, loanId, selectedIntent, selectedPresetKey, submitMessage]);

  // Quick-action click: send the preset's prompt straight to the backend (no input prefill).
  const sendPreset = useCallback(async (promptTemplate, intent, presetKey) => {
    if (!promptTemplate || isStreaming || !loanId) return;
    trackTelemetry('preset_selected', { presetKey: presetKey || '' });
    await submitMessage(promptTemplate, intent || '', presetKey || '');
  }, [isStreaming, loanId, trackTelemetry, submitMessage]);

  const regenerate = useCallback(async () => {
    if (isStreaming || !loanId) return;
    if (!messages.some((m) => m.role === 'user')) return;

    // Drop the last assistant reply locally; the server replaces it too
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      return last?.role === 'assistant' ? prev.slice(0, -1) : prev;
    });

    await trackTelemetry('regenerate_requested');

    await runStream(({ signal, onContent, onStatus, onMessageId }) =>
      regenerateChat({ loanId, token, signal, onContent, onStatus, onMessageId }));
  }, [isStreaming, loanId, messages, token, trackTelemetry, runStream]);

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const clearChat = useCallback(async () => {
    if (isStreaming || !loanId || !token) return;
    setError(null);
    try {
      await clearChatConversation(loanId, token);
      setMessages([]);
      setFeedbackByMessageId({});
      setConversationKey('');
      trackTelemetry('conversation_cleared');
    } catch (err) {
      console.error('Failed to clear conversation:', err);
      setError('Could not clear the conversation.');
    }
  }, [isStreaming, loanId, token, trackTelemetry]);

  const canRegenerate = !isStreaming
    && messages.length > 0
    && messages[messages.length - 1]?.role === 'assistant';

  return {
    messages,
    input,
    setInput,
    streamingContent,
    statusMessage,
    isStreaming,
    isLoadingHistory,
    isLoadingPresets,
    historyError,
    presetsError,
    error,
    presetCategories,
    feedbackByMessageId,
    canRegenerate,
    sendMessage,
    regenerate,
    stopStreaming,
    clearChat,
    sendPreset,
    submitFeedback,
  };
}
